import { PolotnoStoreKeys, usePolotnoState } from '@/pages/AdvancedEditor/hooks/polotno-store';
import { createPolotnoLayers } from '@/services/api/studioApi';
import { useAuth } from '@/services/auth';
import { logger } from '@/services/logger';
import { mapPageToLayer, mapStoreToLayers, prepareLayerData } from '@/services/polotno-parser';
import { hashCode } from '@/utils/utils';
import axios from 'axios';
import { StoreType } from 'polotno/model/store';
import { useCallback, useRef } from 'react';
import { atom, useSetRecoilState } from 'recoil';
import { useProjectRenderer } from './useProjectRenderer';

export const projectLayersLoading = atom<boolean>({
    key: 'projectLayersLoading',
    default: false,
});

export const useCreateProjectLayers = (projectId: string) => {
    const { isAnonymous } = useAuth();
    const [projectPagesImages, setProjectPagesImages] = usePolotnoState(PolotnoStoreKeys.projectPagesImages);
    const [projectPagesHashes, setProjectPagesHashes] = usePolotnoState(PolotnoStoreKeys.projectPagesHashes);
    const setProjectLayersLoading = useSetRecoilState(projectLayersLoading);
    const controllerRef = useRef<AbortController | null>(null);
    const projectRenderer = useProjectRenderer();

    const uploadProjectLayers = useCallback(
        async (store: StoreType) => {
            if (projectRenderer) return;
            if (!isAnonymous) {
                controllerRef.current?.abort();
                const nextController = new AbortController();
                controllerRef.current = nextController;

                try {
                    const mappedStore = mapStoreToLayers(store.toJSON());
                    const mappedPages = store.pages.map(mapPageToLayer);

                    const pagesWithDiffs = mappedPages.filter(page => {
                        const pageHash = hashCode(JSON.stringify({ mappedStore, page }));
                        return projectPagesHashes[page.id] !== pageHash;
                    });

                    const layersByPage = await createPolotnoLayers(
                        projectId,
                        prepareLayerData(mappedStore, pagesWithDiffs),
                        nextController
                    );

                    const layersByPageObject = layersByPage.reduce(
                        (acc, cur, idx) => ({ ...acc, [pagesWithDiffs[idx].id]: { url: cur.background, overlayUrl: cur.overlay } }),
                        {}
                    ) as Record<string, { url: string; overlayUrl: string }>;
                    setProjectPagesImages({ ...projectPagesImages, ...layersByPageObject });

                    controllerRef.current = null;

                    const pagesNewHashes = pagesWithDiffs.reduce((acc, cur) => {
                        const pageHash = hashCode(JSON.stringify({ mappedStore, page: cur }));
                        return { ...acc, [cur.id]: pageHash };
                    }, {});
                    setProjectPagesHashes({ ...projectPagesHashes, ...pagesNewHashes });

                    setProjectLayersLoading(false);
                    return projectPagesImages;
                } catch (error) {
                    if (axios.isCancel(error)) {
                        // debounce cancel - do nothing
                    } else {
                        logger.error('Failed to upload project layers', error);
                        throw error;
                    }
                }
            }
        },
        [
            projectRenderer,
            isAnonymous,
            projectPagesImages,
            projectPagesHashes,
            setProjectPagesHashes,
            setProjectPagesImages,
            setProjectLayersLoading,
        ]
    );

    return {
        uploadProjectLayers: (store: StoreType) => {
            setProjectLayersLoading(true);
            return uploadProjectLayers(store);
        },
    };
};
